// Booth record CRUD over IndexedDB. Every write is local + instant; nothing here
// touches the network (that's Slice 2). saveBooth() is the capture screen's
// single durability call — it persists the record and its photo blobs in one
// transaction-friendly sequence so a reload always finds a complete booth.

import { getDB, withShowBoothKey } from './database';
import type { BoothRecord, StoredPhoto } from './schema';
import type { Show } from '../config';

export interface DraftPhoto {
  stored: StoredPhoto; // bytes for the photos store
}

/** Persist a booth + its photo blobs. Returns the saved record. */
export async function saveBooth(record: BoothRecord, photos: DraftPhoto[]): Promise<BoothRecord> {
  const db = await getDB();

  // Photos first: if the app dies mid-save, an orphan blob is harmless, but a
  // booth record pointing at a missing blob is not. Write bytes, then the record.
  const txPhotos = db.transaction('photos', 'readwrite');
  await Promise.all(photos.map((p) => txPhotos.store.put(p.stored)));
  await txPhotos.done;

  await db.put('booths', withShowBoothKey(record));
  return record;
}

export async function getBooth(id: string): Promise<BoothRecord | undefined> {
  const db = await getDB();
  return db.get('booths', id);
}

/**
 * Persist an edit to an existing booth. Bumps `updated_at` and flips
 * `sync_status` back to 'local' so the sync engine re-pushes it — last-write-wins
 * by `updated_at` then propagates the edit to other devices. Any newly-added
 * photos are written to the photos store first (same ordering as saveBooth).
 */
export async function updateBooth(record: BoothRecord, newPhotos: DraftPhoto[] = []): Promise<BoothRecord> {
  const db = await getDB();
  if (newPhotos.length) {
    const txPhotos = db.transaction('photos', 'readwrite');
    await Promise.all(newPhotos.map((p) => txPhotos.store.put(p.stored)));
    await txPhotos.done;
  }
  record.updated_at = new Date().toISOString();
  record.sync_status = 'local';
  await db.put('booths', withShowBoothKey(record));
  return record;
}

/** All LIVE booths, newest first. Tombstoned (deleted) records are hidden. */
export async function listBooths(): Promise<BoothRecord[]> {
  const db = await getDB();
  const all = await db.getAllFromIndex('booths', 'updated_at');
  return all.reverse().filter((b) => !b.deleted);
}

/** Distinct show names present on LIVE local booths (for the show dropdown seed). */
export async function listBoothShows(): Promise<string[]> {
  const booths = await listBooths();
  const seen = new Set<string>();
  const out: string[] = [];
  for (const b of booths) {
    const s = (b.show ?? '').trim();
    if (s && !seen.has(s.toLowerCase())) {
      seen.add(s.toLowerCase());
      out.push(s);
    }
  }
  return out;
}

/**
 * Tombstone a booth: mark `deleted`, bump `updated_at`, flip to unsynced, and
 * persist. The caller triggers a sync so the tombstone POSTs and other devices
 * hide their copy on the next pull (last-write-wins). We deliberately KEEP the
 * record (soft delete) — a hard delete would be resurrected by the next pull.
 *
 * v0.6.2: the photo BYTES go now (they're the heavy part — iOS storage-eviction
 * risk — and nothing reads them after a delete). The record's `photos[]` refs
 * stay on the tombstone: the backend needs them to know which blobs to purge
 * when the tombstone syncs.
 */
export async function deleteBooth(id: string): Promise<void> {
  const db = await getDB();
  const booth = await db.get('booths', id);
  if (!booth) return;
  await purgeLocalPhotos(id);
  booth.deleted = true;
  booth.updated_at = new Date().toISOString();
  booth.sync_status = 'local';
  await db.put('booths', booth);
}

/**
 * Drop every local StoredPhoto for a booth. Idempotent — deleting an absent key
 * is a no-op, so this is safe to re-run (e.g. the same tombstone pulled twice).
 */
async function purgeLocalPhotos(boothId: string): Promise<number> {
  const photos = await getPhotosForBooth(boothId);
  await Promise.all(photos.map((p) => deleteLocalPhoto(p.local_id)));
  return photos.length;
}

/** Delete a photo's local bytes (used when removing a photo from a saved booth). */
export async function deleteLocalPhoto(localId: string): Promise<void> {
  const db = await getDB();
  await db.delete('photos', localId);
}

export async function getPhotosForBooth(boothId: string): Promise<StoredPhoto[]> {
  const db = await getDB();
  return db.getAllFromIndex('photos', 'booth_id', boothId);
}

/**
 * Live booths for one show, for the exhibitor directory's captured-detection
 * (v0.7). Reads the `show_booth` index is tempting, but that index is keyed per
 * BOOTH — the directory needs the whole show at once (it renders hundreds of rows
 * and would otherwise issue one lookup per booth), so we scan and filter here.
 * Tombstoned records are excluded: a deleted booth is not "captured".
 */
export async function listBoothsForShow(show: Show): Promise<BoothRecord[]> {
  const db = await getDB();
  const all = await db.getAll('booths');
  return all.filter((b) => !b.deleted && b.show === show);
}

/**
 * Count booths still pending sync, for the header/tab badge. Tombstoned records
 * are excluded from the VISIBLE count (per the v0.5 read-path rule) — a pending
 * delete shouldn't nag the user with a badge. It still gets pushed, though:
 * `listUnsyncedBooths` (the drain) deliberately keeps deleted records so the
 * tombstone propagates.
 */
export async function countUnsynced(): Promise<number> {
  const db = await getDB();
  const local = await db.getAllFromIndex('booths', 'sync_status', 'local');
  return local.reduce((n, b) => n + (b.deleted ? 0 : 1), 0);
}

/** Booths not yet pushed to the backend, oldest first (sync in capture order). */
export async function listUnsyncedBooths(): Promise<BoothRecord[]> {
  const db = await getDB();
  const local = await db.getAllFromIndex('booths', 'sync_status', 'local');
  return local.sort((a, b) => a.captured_at.localeCompare(b.captured_at));
}

/**
 * Merge remote booth records (pulled from the backend) into local IndexedDB,
 * keyed by `id`, last-write-wins by `updated_at`. A remote record is written
 * only when there's no local copy or the local copy is older — so a newer local
 * edit that hasn't synced yet is never clobbered. Merged records are marked
 * 'synced' (they came from the server) and carry the denormalised show_booth_key.
 * Returns the number of records actually written.
 *
 * Tombstones ride this path unchanged: a remote `deleted:true` record with a
 * newer `updated_at` overwrites the local copy, which the read paths
 * (`listBooths`/`countUnsynced`/detail) then hide. We still STORE it so the
 * tombstone keeps propagating; the hiding is a read-time concern, not a merge one.
 *
 * v0.6.2: storing an incoming tombstone also purges THIS device's local photo
 * bytes for that booth — a delete made on device A has to free storage on device
 * B too, or B keeps orphaned blobs for a booth it can no longer see.
 */
export async function mergeRemoteBooths(remote: BoothRecord[]): Promise<number> {
  const db = await getDB();
  let upserted = 0;
  for (const r of remote) {
    const local = await db.get('booths', r.id);
    // Skip when local is same-or-newer: never let a stale pull overwrite a fresh
    // local edit. String compare is safe — updated_at is ISO 8601 (lexical = chrono).
    if (local && local.updated_at >= r.updated_at) continue;
    if (r.deleted) await purgeLocalPhotos(r.id);
    await db.put('booths', withShowBoothKey({ ...r, sync_status: 'synced' }));
    upserted++;
  }
  return upserted;
}

/** Mark a booth synced (the record itself is unchanged otherwise). */
export async function markBoothSynced(id: string): Promise<void> {
  const db = await getDB();
  const booth = await db.get('booths', id);
  if (!booth) return;
  booth.sync_status = 'synced';
  await db.put('booths', booth);
}

/**
 * Mark a photo synced and record its server URL — on both the photos store and
 * the owning booth's PhotoRef, so the booth record we push next carries the URL.
 */
export async function markPhotoSynced(localId: string, blobUrl: string): Promise<void> {
  const db = await getDB();
  const photo = await db.get('photos', localId);
  if (photo) {
    photo.synced = true;
    await db.put('photos', photo);
    const booth = await db.get('booths', photo.booth_id);
    if (booth) {
      const ref = booth.photos.find((r) => r.local_id === localId);
      if (ref) {
        ref.synced = true;
        ref.blob_url = blobUrl;
        await db.put('booths', booth);
      }
    }
  }
}
